import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { Rocket, CheckCircle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EarlyAccess = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    userType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.userType) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/early-access`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        toast.success('Successfully joined the waitlist!');
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({ name: '', email: '', userType: '' });
          setSubmitted(false);
        }, 3000);
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Email already registered for early access');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
      console.error('Signup error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="early-access" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a2e] via-[#0a0a2e] to-[#050510]"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6">
        <div className="text-center mb-12 space-y-4">
          <span className="px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-pink-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium inline-block backdrop-blur-sm">
            Join the Revolution
          </span>
          <h2 className="text-4xl lg:text-6xl font-bold text-white">
            Get
            <span className="bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-500 bg-clip-text text-transparent"> Early Access</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Be among the first to experience the future of content creator protection. 
            Join our exclusive early access program.
          </p>
        </div>

        <Card className="relative bg-gradient-to-br from-gray-900/90 to-gray-800/90 border-gray-700/50 backdrop-blur-xl p-8 md:p-12 shadow-2xl overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-500/10 rounded-full blur-3xl"></div>

          {submitted ? (
            <div className="relative z-10 text-center py-12 space-y-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-3xl font-bold text-white">You're on the list!</h3>
              <p className="text-gray-300 text-lg max-w-md mx-auto">
                Thank you for joining Stream Shares. We'll notify you as soon as we launch.
              </p>
              <div className="flex items-center justify-center gap-2 text-cyan-400">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                <span className="text-sm">Early Access Confirmed</span>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="relative z-10 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-300 text-lg">
                    Full Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 h-12 text-lg focus:border-cyan-500 focus:ring-cyan-500/20"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300 text-lg">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500 h-12 text-lg focus:border-cyan-500 focus:ring-cyan-500/20"
                    required
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-gray-300 text-lg">
                  I am a...
                </Label>
                <div className="grid md:grid-cols-2 gap-4">
                  {['Creator', 'Viewer'].map((type) => (
                    <label
                      key={type}
                      className={`relative flex items-center justify-center p-6 rounded-xl border-2 cursor-pointer transition-all duration-300 z-20 ${
                        formData.userType === type
                          ? 'border-cyan-500 bg-gradient-to-br from-cyan-500/20 to-pink-500/20 shadow-lg shadow-cyan-500/20'
                          : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="userType"
                        value={type}
                        checked={formData.userType === type}
                        onChange={handleChange}
                        className="absolute inset-0 opacity-0 cursor-pointer z-30"
                        required
                      />
                      <span className={`text-lg font-medium pointer-events-none ${
                        formData.userType === type ? 'text-cyan-400' : 'text-gray-400'
                      }`}>
                        {type}
                      </span>
                      {formData.userType === type && (
                        <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-cyan-400 pointer-events-none" />
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                size="lg"
                className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 hover:from-cyan-600 hover:to-pink-600 text-white text-lg py-6 border-0 group shadow-lg shadow-cyan-500/30"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Joining Waitlist...
                  </>
                ) : (
                  <>
                    <Rocket className="mr-2 group-hover:translate-y-[-2px] transition-transform" />
                    Join the Waitlist
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>

              <p className="text-center text-gray-500 text-sm">
                By joining, you'll be the first to know when we launch and get exclusive early access benefits.
              </p>
            </form>
          )}
        </Card>

        {/* Benefits Grid */}
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            { icon: "🎯", title: "Early Access", description: "Be first to use the platform" },
            { icon: "💎", title: "Exclusive Benefits", description: "Special features for early adopters" },
            { icon: "🤝", title: "Shape the Future", description: "Your feedback matters" }
          ].map((benefit, index) => (
            <div 
              key={index}
              className="text-center p-6 rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 border border-gray-700/30 backdrop-blur-sm hover:border-cyan-500/30 transition-all"
            >
              <div className="text-4xl mb-3">{benefit.icon}</div>
              <h4 className="text-lg font-bold text-white mb-2">{benefit.title}</h4>
              <p className="text-gray-400 text-sm">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EarlyAccess;
